<template>
  <v-app id="test-order-app">
    <HeaderSection />
    <v-main>
      <v-container>
        <div v-if="!completed">
          <div class=" ">
            <v-alert
              v-if="!isProdEnv"
              color="red"
              style="color: black; border-radius: none !important"
              class="pl-4 pr-4"
            >
              This site is not for production use.
            </v-alert>
          </div>
          <div>
            <v-alert
              v-if="alertOne"
              v-model="alertOne"
              color="#a0cceb"
              dismissible
              style="color: black; border-radius: none !important"
              class="pl-4 pr-4"
            >
              Test requests via this system cannot be partially completed and
              returned to later.
            </v-alert>
          </div>
          <v-row v-if="showExpansionPanel">
            <v-col class="custom-col">
              <FirstTimeUserInfoExpansion />
            </v-col>
          </v-row>
          <form-wizard
            id="wizard-form-steps"
            :title="title"
            @on-complete="handleSubmitBtnClicked()"
            ref="wizard"
            :subtitle="subtitle"
            :disabled="isSubmitBtnClicked"
            :color="wizardColour"
            buttonPrevDisplay="buttonPrevDisplay"
          >
            <tab-content
              id="test-type-section"
              title="Test type"
              :before-change="() => store.testCategoryChosen()"
            >
              <TestType ref="testType"></TestType>
            </tab-content>
            <tab-content
              id="practitioner-section"
              title="Practitioner details"
              :before-change="
                () => validatePatientOrPractitioner('practitioner')
              "
            >
              <Practitioner
                :reusePractitionerData="reusePractitionerData"
                ref="practitioner"
                :error="hasErrored ? errorReason : null"
              >
              </Practitioner>
            </tab-content>
            <tab-content
              title="Patient details"
              :color="wizardColour"
              :before-change="() => validatePatientOrPractitioner('patient')"
            >
              <PatientDetails
                :reusePatientData="reusePatientData"
                title="Patient details"
                ref="patient"
                :error="hasErrored ? errorReason : null"
              />
            </tab-content>
            <tab-content id="test-wizard-tab" title="Test details">
              <v-expansion-panels multiple v-model="store.panel">
                <Tests
                  ref="tests"
                  :patientdata="patientData"
                  @hasTestsShown="hasAddedTests"
                  :force-form-dirty="hasAttemptedSubmit"
                  :showReanalysisAlert="showReanalysisAlert"
                  @updateAppUploadFiles="updateAppUploadFiles"
                  @updateSelectedTestsArr="updateSelectedTestsArr"
                  :error="hasErrored ? errorReason : null"
                  :showVariantReviewAlert="showVariantReviewAlert"
                  :hasMultiTrioTests="hasMultiTrioTests"
                  :submittingMultiTrio="submittingMultiTrio"
                />
              </v-expansion-panels>
            </tab-content>
            <template
              slot="footer"
              slot-scope="{ activeTabIndex, isLastStep, nextTab, prevTab }"
            >
              <div class="wizard-footer-left pt-12">
                <v-btn
                  v-if="activeTabIndex > 0"
                  @click="
                    prevTab();
                    handleprev(activeTabIndex);
                  "
                  type="button"
                  class="secondary-btn btn-padding-large"
                >
                  Return
                </v-btn>
              </div>
              <div
                :class="
                  activeTabIndex === 0
                    ? 'pt-16 center'
                    : 'wizard-footer-right pt-12'
                "
              >
                <v-btn
                  v-if="!isLastStep && store.isPrenatal !== null"
                  id="next-wizard-step"
                  @click="
                    nextTab();
                    scrollTop();
                  "
                  class="primary-btn btn-padding-large"
                >
                  Next
                </v-btn>
                <v-btn
                  v-else-if="store.isPrenatal !== null"
                  id="finish-button"
                  @click="
                    nextTab();
                    scrollTop();
                  "
                  type="button"
                  :color="
                    addedTests
                      ? 'primary-btn btn-padding-large'
                      : 'secondary-btn btn-padding-large'
                  "
                >
                  Finish
                </v-btn>
              </div>
            </template>
          </form-wizard>
          <v-dialog
            v-model="isSubmitting"
            persistent
            max-width="500"
            class="center"
          >
            <v-card class="loading-card">
              <div class="loading-content">
                <v-progress-circular
                  indeterminate
                  color="primary"
                  size="64"
                ></v-progress-circular>
                <p>Please wait, your test request is being sent to VCGS.</p>
              </div>
            </v-card>
          </v-dialog>
        </div>
        <div v-else>
          <Completed
            class="form-grid"
            :formdata="completedFormData"
            :appAllConsentFiles="appAllConsentFiles"
            :appAllPrevReportFiles="appAllPrevReportFiles"
            :showReanalysisAlert="showReanalysisAlert"
            :showVariantReviewAlert="showVariantReviewAlert"
          />
        </div>
      </v-container>
    </v-main>
    <FooterSection />
  </v-app>
</template>

<script>
import HeaderSection from "./components/HeaderSection";
import FooterSection from "./components/FooterSection";
import Practitioner from "./components/Practitioner";
import PatientDetails from "./components/PatientDetails";
import Tests from "./components/Tests";
import Completed from "./components/Completed";
import { EventBus } from "./event-bus.js";
import DropDownMixin from "./mixins/DropDownMixin";
import FirstTimeUserInfoExpansion from "@/components/FirstTimeUserInfoExpansion";
import axios from "axios";
import { useCompletedDetails, useSectionStore } from "./store/SectionStore";
import { FormWizard, TabContent } from "vue-form-wizard";
import "vue-form-wizard/dist/vue-form-wizard.min.css";
import TestType from "./components/TestType.vue";
import { v4 as uuidv4 } from "uuid";
import * as Sentry from "@sentry/browser";

export default {
  name: "App",
  mixins: [DropDownMixin],
  components: {
    FirstTimeUserInfoExpansion,
    HeaderSection,
    FooterSection,
    Practitioner,
    PatientDetails,
    Tests,
    Completed,
    FormWizard,
    TabContent,
    TestType,
  },
  setup() {
    const store = useSectionStore();
    const completedStore = useCompletedDetails();
    return {
      completedStore,
      store,
    };
  },
  created() {
    EventBus.$on("resetFormWizard", () => {
      this.$refs.wizard.reset();
    });
  },
  mounted() {
    EventBus.$on("selectTrioTest", (testName) => {
      this.selectedTrioTests.push(testName);
    });
    EventBus.$on("deselectTrioTest", (testName) => {
      this.selectedTrioTests = this.selectedTrioTests.filter(
        (item) => item !== testName,
      );
    });
    EventBus.$on("getPatientData", () => {
      if (
        typeof this.$refs["patient"] !== "undefined" &&
        this.$refs["patient"] !== null
      ) {
        this.patientData = this.$refs["patient"].transferData();
      }
    });

    EventBus.$on("reusePractitionerAndPatientDetails", (reuseData) => {
      this.orderId = uuidv4();
      this.completed = false;
      this.reusePractitionerData = reuseData.reusePractitionerData;
      this.reusePatientData = reuseData.reusePatientData;
      this.famSampleTypes = [];
      this.selectedTestsArr = [];
    });

    EventBus.$on("addFamSampleType", (famSampleTypeObj) => {
      this.famSampleTypes.push(famSampleTypeObj);
    });

    EventBus.$on("removeFamSampleType", ({ testType, title }) => {
      if (!this.completed) {
        this.famSampleTypes = this.famSampleTypes.filter((sampleTypeObj) => {
          return !(
            sampleTypeObj.testType === testType && sampleTypeObj.title === title
          );
        });
      }
    });

    EventBus.$on("updateShowVariantReviewAlert", (showAlert) => {
      this.showVariantReviewAlert = showAlert;
    });

    EventBus.$on(
      "trioTargetedPrenatalTestAlert",
      this.handleTrioTargetedPrenatalTestAlert,
    );

    EventBus.$on("handleSubmittingMultiTrio", (bool) => {
      this.submittingMultiTrio = bool;
    });
  },

  data: () => ({
    alertOne: true,
    alertTwo: true,
    schemaVersion: "1.0.0",
    patientData: {},
    hasErrored: false,
    addedTests: false,
    errorReason: "Something went wrong",
    completed: false,
    completedFormData: {},
    isSubmitting: false,
    reusePractitionerData: {},
    reusePatientData: {},
    hasAttemptedSubmit: false,
    isSubmitBtnClicked: false,
    title: "",
    subtitle: "",
    finishColour: "green",
    wizardColour: "#1FA5FF",
    nextColour: "#0081D6",
    showExpansionPanel: true,
    buttonPrevDisplay: false,
    orderId: uuidv4(),
    appAllPrevReportFiles: [],
    appAllConsentFiles: [],
    documentsPresent: false,
    famSampleTypes: [],
    selectedTestsArr: [],
    showVariantReviewAlert: false,
    selectedTrioTests: [],
    isTrioTargetedPrenatalExomeTest: false,
    submittingMultiTrio: false,
  }),
  computed: {
    hasMultiTrioTests() {
      return this.selectedTrioTests.length > 1;
    },
    isProdEnv() {
      return process.env.VUE_APP_ENVIRONMENT === "prod";
    },
    isFamSampleStoredData() {
      return this.famSampleTypes.length > 0;
    },
    isPatientSampleStoredData: function() {
      if (this.patientData && this.patientData.sampleType === "STORED_DATA") {
        return true;
      }

      return false;
    },
    showReanalysisAlert: function() {
      if (this.selectedTestsArr.length === 0) return false;

      if (
        this.isPatientSampleStoredData ||
        this.isFamSampleStoredData ||
        this.isTrioTargetedPrenatalExomeTest
      )
        return true;

      return false;
    },
  },
  methods: {
    handleTrioTargetedPrenatalTestAlert(value) {
      this.isTrioTargetedPrenatalExomeTest = value;
    },
    updateSelectedTestsArr(newTestsArr) {
      this.selectedTestsArr = newTestsArr;
    },
    updateAppUploadFiles(newUploadFiles, uploadFilesName) {
      switch (uploadFilesName) {
        case "allPrevReportFiles":
          this.appAllPrevReportFiles = newUploadFiles;
          return;
        case "allConsentFiles":
          this.appAllConsentFiles = newUploadFiles;
          return;
      }
    },
    async recaptcha() {
      // save the real recaptha testing for the formally deployed test sites.. otherwise just skip
      if (this.$dataset.isFrontEndOnly) {
        return "NOTAREALCAPTCHA";
      } else {
        // (optional) Wait until recaptcha has been loaded.
        await this.$recaptchaLoaded();

        // Execute reCAPTCHA with action "login".
        return await this.$recaptcha("login");
      }
    },
    async handleSubmitBtnClicked() {
      // this is to stop the `Submit` button from submitting multiple times
      if (this.isSubmitBtnClicked) return;

      this.isSubmitBtnClicked = true;
      this.isSubmitting = true;

      // If the order contains multi trio tests, prevent it from submitting
      if (this.hasMultiTrioTests) {
        this.submittingMultiTrio = true;
        this.isSubmitting = false;
        this.isSubmitBtnClicked = false;

        return;
      }

      try {
        await this.sendJson();
      } catch (error) {
        console.error("Error caught when handling submission", error);
        Sentry.captureException(error);
      }

      this.isSubmitBtnClicked = false;
      this.isSubmitting = false;
    },
    scrollTop() {
      this.$vuetify.goTo("#wizard-form-steps");
    },
    handleprev(index) {
      if (index === 1) {
        this.showExpansionPanel = true;
      }
      this.scrollTop();
    },
    validatePatientOrPractitioner(formType) {
      if (formType === "practitioner") {
        this.showExpansionPanel = false;
      }
      let validated = true;
      let component = this.$refs[formType];
      if (!component.validateForm()) {
        validated = false;
      }
      if (!validated) {
        this.hasErrored = true;
        this.errorReason = "Please complete indicated fields.";
      }
      if (!component.isFormValid()) {
        validated = false;
        this.hasErrored = true;
      }
      if (validated) {
        this.hasErrored = false;
        this.$vuetify.goTo("#wizard-form-steps");
      }
      return validated;
    },
    showPrivateOnlyAlert() {
      const privateOnlyTests = ["alport", "renal_cystic", "renal_non_cystic"];

      // If any selected test is included in the privateOnlyTests array, isPrivateOnlyTest is true
      const isPrivateOnlyTest = privateOnlyTests.some((test) =>
        this.selectedTestsArr.includes(test),
      );

      let showPrivateOnlyAlert;
      if (isPrivateOnlyTest) {
        if (
          this.patientData.patientStatusContent === "public_public_hospital" ||
          this.patientData.patientStatusContent ===
            "public_outpatient_public_hospital"
        ) {
          showPrivateOnlyAlert = true;
        } else {
          showPrivateOnlyAlert = false;
        }
      } else {
        showPrivateOnlyAlert = false;
      }

      return showPrivateOnlyAlert;
    },
    checkDocumentsPresent() {
      if (
        this.appAllConsentFiles.length > 0 ||
        this.appAllPrevReportFiles.length > 0
      )
        return (this.documentsPresent = true);

      return (this.documentsPresent = false);
    },
    async sendJson() {
      let currDate = this.getMelbTime();

      // by attempting a submit we are saying to all children forms that they should show warnings about
      // empty/invalid values
      // this is the 'correct' mechanism going forward - there are still some legacy bits that work via a
      // trigger sideeffect from isFormValid - which is working ok but is not correct
      this.hasAttemptedSubmit = true;
      this.hasErrored = false;

      this.checkDocumentsPresent();

      let tests = this.$refs["tests"];
      let dataObj = {
        dateSubmitted: currDate,
        orderIdFull: this.orderId,
        orderId: this.orderId
          .substring(this.orderId.length - 6, this.orderId.length)
          .toUpperCase(),
        documentsPresent: this.documentsPresent, // This property indicates whether there are uploaded files.
        websiteVersion: process.env.VUE_APP_BUILD_VERSION || "",
        schemaVersion: this.schemaVersion,
      };
      let refNames = ["practitioner", "patient", "tests", "billing"];
      let validated = true;
      for (let name of refNames) {
        if (name in this.$refs) {
          if (name === "tests" && !useSectionStore().hasTests()) {
            // Check if they added any tests
            this.hasErrored = true;
            this.errorReason = "You need to add some tests to continue";
            return;
          } else if (name === "tests" && this.showPrivateOnlyAlert()) {
            this.hasErrored = true;
            this.errorReason =
              "You cannot order a Medicare test for a public patient.";
            return;
          } else if (this.getInvalidTestsList().length > 0) {
            this.hasErrored = true;
            this.errorReason = `The tests: ${this.getInvalidTestsList().join(
              ", ",
            )} are not supported by sample type: ${this.sampleTypeName(
              this.patientData.sampleType,
            )}`;
            return;
          } else if (this.familyMemberInvalidTestAndSampleType().length > 0) {
            // getting not supported Sample Type in the test with family member.
            this.hasErrored = true;
            this.errorReason = "";
            this.familyMemberInvalidTestAndSampleType().forEach(
              // for each error, display it out
              (invalidFamilyMember) => {
                this.errorReason += `The family member with role: ${
                  invalidFamilyMember.personType
                } in test: ${tests.getTestName(
                  invalidFamilyMember.test,
                )} has invalid sample type: ${this.sampleTypeName(
                  invalidFamilyMember.sampleType,
                )}\n`;
              },
            );
            return;
          } else if (name === "tests") {
            dataObj["totalDisplayPrice"] = this.$refs[
              name
            ].transferTotalPrice();
          }

          let component = this.$refs[name];

          if (!component.validateForm()) {
            if (name === "patient") {
              this.hasErrored = true;
              this.errorReason =
                "Patient medicare details are required for selected tests. Please click the Return button to complete this in the patient details section.";
              return;
            } else {
              validated = false;
            }
          }
          dataObj[name] = component.transferData();
        } else {
          let component = this.$refs["tests"].$refs["billing"];
          if (component) {
            dataObj[name] = component.transferData();
            dataObj[name]["totalDisplayPrice"] = this.$refs[
              "tests"
            ].transferTotalPrice();
          }
        }
      }

      /** This IF statement is to make sure orders that contain tests incl. Childhood Syndromes, Connexin, Mito disease and Variant Detection, can have
       * correct sendBillTo value. Otherwise, if referer selects a different test with PATIENT/OTHER funding source as well as those affected tests
       * but then remove the other test and submit the affected tests the sendBillTo value won't be updated properly
       */
      if (
        dataObj["billing"] &&
        dataObj["billing"]["totalDisplayPrice"] === "$0"
      ) {
        dataObj["billing"]["sendBillTo"] = "MEDICARE";

        /** There is an exception if the order only includes variant detection, it is not Medicare and its family type is Trio
         */
        if (
          dataObj.tests &&
          dataObj.tests.length === 1 &&
          dataObj.tests[0].testType === "variant_detection" &&
          dataObj.tests[0].billTo !== "MEDICARE" &&
          dataObj.tests[0].family.length === 2
        ) {
          dataObj["billing"]["sendBillTo"] = dataObj.tests[0].billTo;
        }
      }

      // debug print the form data because that is useful during dev/test
      if (!this.$dataset.isProductionLike) {
        /* eslint-disable no-console */
        console.log(JSON.stringify(dataObj, null, 2));
        /* eslint-enable no-console */
      }

      if (!validated) {
        this.hasErrored = true;
        this.errorReason = "Please complete indicated fields.";
        const store = useSectionStore();
        store.openAllPanels();
        return;
      }

      if (!dataObj["tests"]) {
        this.hasErrored = true;
        this.errorReason =
          "No tests are present. You need to add some tests to continue.";
        return;
      }

      this.hasErrored = false;

      // Setup doctor details for Sentry in case an error occurs
      Sentry.setContext("practitioner", {
        name:
          dataObj.practitioner.firstName + " " + dataObj.practitioner.lastName,
        email: dataObj.practitioner.emailAddress,
        testCode: dataObj.tests.map((test) => test.testCode).join(" "),
      });

      // we only do a real submission if we know we have a backend to send it to
      // OR if we know we are running in cypress in which case the backend will be stubbed out for us
      if (!this.$dataset.isFrontEndOnly || window.Cypress) {
        // Backend available, do POST
        try {
          dataObj["recaptchaToken"] = await this.recaptcha();

          let response = await axios.post(
            this.$dataset.placeOrderServiceUrl,
            dataObj,
          );

          // only log the response with PII during dev/test
          if (!this.$dataset.isProductionLike) {
            console.log(response);
          }
        } catch (error) {
          this.hasErrored = true;
          console.log(error);

          if (axios.isAxiosError(error)) {
            if (error.response) {
              // 1. Request was made and the server responded with a non-2xx status
              const { status, data } = error.response;

              if (
                status === 403 &&
                typeof data === "string" &&
                data.includes("FAILED_RECAPTCHA")
              ) {
                this.errorReason =
                  "The form submission failed the reCaptcha security check. Please refresh the page and restart your order.";
              } else if (
                status === 500 &&
                typeof data === "string" &&
                data.includes("SQS failure")
              ) {
                this.errorReason =
                  `The form submission encountered issue, not 2xx response, instead got ${status} ` +
                  `and response data ${JSON.stringify(data)}. ` +
                  `Please contact us to inform us of this error.`;
              } else {
                this.errorReason =
                  `Could not submit, not 2xx response, instead got ${status} ` +
                  `and response data ${JSON.stringify(data)}. ` +
                  `Please contact us to inform us of this error.`;
              }
            } else if (error.request) {
              // 2. Request was made but no response was received
              switch (error.code) {
                case "ERR_NETWORK":
                  this.errorReason =
                    "We have issue connecting to the service. Please check your network and try again.";
                  break;

                case "ECONNABORTED":
                case "ETIMEDOUT":
                  this.errorReason = "The request timed out. Please try again.";
                  break;

                case "ERR_CANCELED":
                  this.errorReason =
                    "The request was cancelled. Please try again.";
                  break;

                default:
                  this.errorReason =
                    "Could not submit, no response received for API call. Please contact us to inform us of this error.";
              }
            } else {
              // 3. Something happened while setting up the request
              switch (error.code) {
                case "ERR_INVALID_URL":
                  this.errorReason =
                    "Unable to connect to the service. Please contact us to inform us of this error.";
                  break;

                default:
                  this.errorReason =
                    error.message ||
                    `Form submission failed with error code: ${error.code}. Please contact us to inform us of this error.`;
              }
            }
          } else {
            this.errorReason = `Something unexpected went wrong. Error code: ${error.code} and error message: ${error.message}. Please contact us to inform us of this error.`;
          }

          Sentry.captureException(this.errorReason);
          return;
        }
      }

      // Setup doctor details for Sentry in case an error occurs
      Sentry.setContext("practitioner", {
        name:
          dataObj.practitioner.firstName + " " + dataObj.practitioner.lastName,
        email: dataObj.practitioner.emailAddress,
        testCode: dataObj.tests.map((test) => test.testCode).join(" "),
      });

      this.completed = true;
      this.completedFormData = dataObj;
    },

    hasAddedTests(val) {
      this.addedTests = val;
    },
    getInvalidTestsList: function() {
      let val = this.patientData.sampleType;
      for (let sample of this.sampleTypeList) {
        if (sample.value === val) {
          let tests = this.$refs["tests"].getTestsList();
          let invalidTests = tests.filter(
            (el) => sample.validTests.indexOf(el) < 0,
          );
          invalidTests = invalidTests.map((invalidTest) => {
            return this.$refs["tests"].getTestName(invalidTest);
          });
          return invalidTests;
        }
      }
      return [];
    },
    familyMemberInvalidTestAndSampleType() {
      // find all the form components related to tests
      let tests = this.$refs["tests"];
      let testsData = tests.transferData();

      // getting list of invalid combos of family member and test
      let list = [];

      testsData.forEach((test) => {
        // `testType` value is `testId` prop value for each test component and it will always have a string value
        if (test.testType.includes("prenatal")) {
          // For the moment any test is valid for parents of a prenatal test
          return [];
        }
        // per test
        else if ("family" in test) {
          let families = test.family;
          families.forEach((family) => {
            // per family member
            this.sampleTypeList
              .filter((sample) => sample.value === family.sampleType)
              .forEach((sample) => {
                if (!sample.validTests.includes(test.testType)) {
                  // add to the list of of invalid combos
                  list.push({
                    personType: family.personType,
                    sampleType: family.sampleType,
                    test: test.testType,
                  });
                }
              });
          });
        }
      });
      return list;
    },
  },
};
</script>

<style>
table,
th,
td {
  padding: 1px;
  table-layout: fixed;
  width: 100%;
}
hr {
  width: 100%;
  height: 2px;
  background-color: black;
  margin-right: auto;
  margin-left: auto;
  margin-top: 5px;
  margin-bottom: 5px;
}
h1 {
  font-family: "Avenir", sans-serif;
  font-size: 50px;
  line-height: 27px;
  color: #2998c6;
}

h2 {
  font-family: "Avenir", sans-serif;
  font-size: 28px;
  line-height: 24px;
  font-weight: 400;
  color: #2998c6;
}
.heading {
  font-family: "Avenir", sans-serif;
  font-weight: 500;
  color: #00538b;
}

h3 {
  font-family: "Avenir", sans-serif;
  font-size: 20px;
  line-height: 24px;
  font-weight: 500;
  color: #2998c6;
}
h2.flex {
  text-align: center;
  color: black;
  align-content: center;
  font-family: "Arial", "Verela", "Varela Round", sans-serif;
  font-size: 30px;
}

span.txt {
  color: #515151;
  font-size: 16px;
}

.sticky {
  position: sticky;
  justify-content: right;
}
.form-grid {
  border-radius: 5px !important;
  border-style: solid;
  border-width: 2px;
  border-color: #00aeef;
  padding: 8px;
  margin-bottom: 2em;
}

.v-input__slot {
  margin-top: 2px !important;
  min-height: 4px !important;
}

.v-input__append-inner {
  margin-top: 10px !important;
}

.v-select.v-select--chips:not(.v-text-field--single-line).v-text-field--box
  .v-select__selections,
.v-select.v-select--chips:not(.v-text-field--single-line).v-text-field--enclosed
  .v-select__selections {
  min-height: 46px !important;
}

.v-select.v-text-field--outlined:not(.v-text-field--single-line)
  .v-select__selections {
  padding: 6px 0 !important;
}

.v-application .error--text {
  color: blue !important;
  caret-color: blue !important;
}

.v-text-field__slot {
  min-height: 44px !important;
  padding: 6px 4px;
}

.v-text-field__details {
  padding-left: 0px !important;
}

.v-text-field__hint {
  position: absolute;
  bottom: -40px;
  left: 8px;
}
.form-no-border .v-input__slot {
  border-style: none;
}
.text-hint {
  display: block;
  margin-top: 8px;
  font-size: 14px;
  color: #999;
  align-self: flex-end;
}

.v-btn {
  text-transform: none !important;
}

.v-input__slot::before {
  border: none !important;
}

.v-input__slot::after {
  border: none !important;
}

.top-spaced-item {
  margin-top: 2em;
}

.left-spaced-item {
  margin-left: 1em;
}

.multi-line {
  white-space: pre-line;
}

.v-input--selection-controls {
  margin-top: 0 !important;
  padding-top: 0 !important;
}

.v-input--selection-controls .v-messages {
  min-height: 0 !important;
}

.address-class {
  padding: 0px !important;
}

.wizard-footer-left {
  padding-left: 0px;
}
.wizard-footer-right {
  padding-right: 0px;
}

@media screen and (max-width: 1024px) {
  .grid-container {
    display: flex !important;
    flex-wrap: wrap !important;
  }

  .grid-container > :not(.small-button) {
    width: 100% !important;
    padding: 10px;
  }

  h1 {
    font-family: "Arial", "Verela", "Varela Round", sans-serif;
    font-size: 35px;
    color: #2998c6;
    font-weight: 200;
  }
  h3 {
    font-family: "Arial", "Verela", "Varela Round", sans-serif;
    font-size: 25px;
    color: #2998c6;
  }
  .form-grid {
    border: none !important;
    padding: 1%;
    margin-bottom: 20px;
  }
}
.v-select {
  z-index: 30;
}
.v-alert {
  margin-top: 25px;
  text-justify: auto;
}
.rcorners {
  border-radius: 25px !important;
}
.text-title {
  color: #555;
  margin-bottom: 4px;
  font-size: 16px;
}
.custom-text-field {
  border: 1px solid #ccc;
  padding: 10px;
  line-height: 46px;
  min-height: 46px;
  width: 100%;
  box-sizing: border-box;
  white-space: pre-wrap;
  overflow-wrap: break-word;
  resize: none;
  display: inline-block;
}
.primary-btn {
  background: #00538b !important;
  color: #fff !important;
  box-shadow: none !important;
  letter-spacing: 0.4px !important;
}
.secondary-btn {
  border: 1px solid #00538b;
  background: #fff !important;
  color: #00538b !important;
  box-shadow: none !important;
  letter-spacing: 0.4px !important;
}

.btn-padding-large {
  padding-left: 40px !important;
  padding-right: 40px !important;
  padding-top: 25px !important;
  padding-bottom: 25px !important;
}
.btn-padding-medium {
  padding-left: 30px !important;
  padding-right: 30px !important;
  padding-top: 22px !important;
  padding-bottom: 22px !important;
}

.p {
  font-family: "Avenir", sans-serif;
}

.custom-label p {
  margin: 0;
  padding: 0;
  display: flex;
  align-items: center;
}

.v-application .v-alert.custom-alert {
  background-color: transparent !important;
  border: 1px solid #ff0000 !important;
}

.v-application .v-alert.custom-alert .v-alert__content {
  color: #bf0000 !important;
}
.center {
  text-align: center !important;
  position: relative;
  padding-top: 18px;
  padding-bottom: 8px;
}
.middle {
  text-align: center !important;
  position: relative;
}

.top-bottom-margin {
  margin-top: 5px;
  margin-bottom: 5px;
}

@media (max-width: 960px) {
  .custom-col {
    flex-basis: 100% !important;
    max-width: 100% !important;
  }
}
.v-text-field--outlined .v-label {
  top: auto !important;
}
.text-grey {
  background-color: #e5e5e5 !important;
  color: grey !important;
  outline-width: 1px !important;
}

.border-grey {
  border-radius: 4px !important;
  border-style: solid !important;
  border-width: 1px !important;
  border-color: grey !important;
}
.warning-orange {
  background-color: transparent !important;
  color: orange !important;
}

.border-orange {
  border-radius: 4px !important;
  border-style: solid !important;
  border-width: 1px !important;
  border-color: orange !important;
}

.loading-card {
  background-color: white !important;
  color: black !important;
  padding: 40px !important;
  text-align: center !important;
  box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1) !important;
}

.loading-content {
  display: flex !important;
  flex-direction: column !important;
  align-items: center !important;
  gap: 40px !important;
}
</style>
